import {
  Button,
  Grid,
  InputLabel,
  IconButton,
  FormHelperText,
  Tooltip,
  Box,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import InfoIcon from "@mui/icons-material/Info";
import "react-datepicker/dist/react-datepicker.css";
import ExcelIcon from "../../assets/images/ExcelExport.png";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import { AgGridReact } from "ag-grid-react";
import { Col, Form, Row } from "react-bootstrap";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getActivityReportDetails,
  getActivityReportScheduleGrid,
  getActivityReportsData,
  scheduleActivityReportEdit,
} from "../../api/activityReportApi/activityReportApi";
import { useLoader } from "../../context/LoaderContext";
import ScheduleActivityReport from "./ScheduleActivityReport";
import { useNavigate } from "react-router-dom";
import { ColDef } from "ag-grid-enterprise";
import ActivityReportDetailModal from "./ActivityReportDetailModal";
import { getExcelData } from "../../api/groupsApi/groupsApi";
import { appSettings } from "../../config/appSetting";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import moment from "moment";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

interface ActivityReportProps {
  businessUnitData: any;
}

interface ActionsProps {
  data: any;
  businessUnitData: any;
  onDetailsAction: (data: any) => void;
}

const Actions: React.FC<ActionsProps> = ({
  data,
  businessUnitData,
  onDetailsAction,
}) => {
  const navigate = useNavigate();

  if (data === undefined) {
    return null;
  }

  const handleDetailsAction = () => {
    navigate("", {
      state: { rowData: data, action: "Details" },
    });
    onDetailsAction(data);
  };

  return (
    <>
      {isPagePermission(
        "Allow Download Detail View",
        "ActivityReport",
        1381
      ) && (
        <Tooltip title="Click to View the Activity Details">
          <InfoIcon
            className="editIcon"
            onClick={handleDetailsAction}
            style={{
              padding: "0.2vh",
              color: "var(--aggrid-text-color)",
              cursor: "pointer",
            }}
          />
        </Tooltip>
      )}
    </>
  );
};

const ActivityReport: React.FC<ActivityReportProps> = ({
  businessUnitData,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [startDateError, setStartDateError] = useState(false);
  const [endDateError, setEndDateError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectReport, setSelectReport] = useState<any>([]);
  const [rowData, setRowData] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedData, setSelectedData] = useState<any>();
  const [selectedButton, setSelectedButton] = useState<string | null>(
    "ACTIVITY SUMMARY"
  );
  const [activityReport, setActivityReport] = useState([]);
  const [scheduleReportId, setScheduleReportId] = useState();
  const [formData, setFormData] = useState({
    scheduleReportId:
      selectReport?.length > 0 ? selectReport[0]?.scheduleReportId : "",
  });

  const { setLoading } = useLoader();
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
  };

  const handleModalClose = (confirmed: boolean) => {
    if (confirmed) {
      console.log("Modal confirmed!");
    }
    setOpenDetailsModal(false);
  };

  const handleDetailsAction = async (selectedRowData: any) => {
    setOpenDetailsModal(true);
    if (selectedRowData) {
      const payload = {
        fromDate: selectedRowData?.fromDate,
        timezone: appSettings.timezone,
        toDate: selectedRowData?.toDate,
        contextName:
          businessUnitData?.contextName ||
          LocalStorageUtil.getItem("contextName"),
        documentStreamId: selectedRowData?.documentstreamid,
      };
      try {
        const response = await getActivityReportDetails(payload);
        setSelectedData(response);
      } catch (error) {
        console.error("Error fetching activity report details:", error);
      }
    }
  };

  const formatUploadedOnDate = (value: any) => {
    return value ? moment.utc(value).local().format("MM-DD-YYYY HH:mm:ss") : "";
  };

  const actionsCellRenderer = (params: any) => (
    <Actions
      data={params.data}
      businessUnitData={businessUnitData}
      onDetailsAction={handleDetailsAction}
    />
  );

  const columnDefs = useMemo(() => {
    const headersDisplayNames: Record<string, string> = {
      documentID: "Document ID",
      fileName: "File Name",
      downloadCount: "No.of Downloads",
      uploadedOn: "Uploaded On",
      uploadedBy: "Uploaded By",
      size: "Size(in KB)",
    };
    const predefinedColumns: ColDef[] = [
      {
        width: 50,
        sortable: false,
        filter: false,
        colId: "checkboxSelection",
      },
      {
        headerName: "#",
        width: 90,
        sortable: false,
        filter: false,
        resizable: false,
        colId: "rowIndex",
        valueGetter: (params: any) =>
          params.node ? (params.node.rowIndex ?? 0) + 1 : 0,
      },
      ...(selectedButton === "ACTIVITY DETAILS"
        ? [
            {
              headerName: "Details",
              field: "link",
              cellRenderer: actionsCellRenderer,
              lockPinned: true,
              suppressSizeToFit: true,
              width: 130,
              pinned: "right" as const,
            },
          ]
        : []),
    ];
    const generateDynamicColumns = (
      rowData: any[],
      selectedHeaders: string[]
    ): ColDef[] => {
      if (!rowData || rowData.length === 0) {
        return [];
      }

      const columnsToExclude = [
        "activityReportDetailExcel",
        "documentstreamid",
        "currentPage",
        "isexporttpExcel",
        "sortColumn",
        "contextName",
        "fromDate",
        "toDate",
        "totalRecords",
        "sortOrder",
        "noOfRecords",
        "recordCounts",
      ];

      return Object.keys(rowData[0])
        .filter(
          (key) =>
            selectedHeaders.includes(key) && !columnsToExclude.includes(key)
        )
        .map((key) => {
          const columnDef: ColDef = {
            headerName: headersDisplayNames[key] || key,
            field: key,
            tooltipField: key,
            width: 300,
            colId: key,
            sortable: true,
            filter: true,
          };
          if (key === "uploadedOn") {
            columnDef.valueFormatter = formatUploadedOnDate;
          }
          return columnDef;
        });
    };

    const generateHeadersMap = (rowData: any[]): Record<string, string[]> => {
      if (!rowData || rowData.length === 0) {
        return {};
      }
      const availableHeaders = Object.keys(rowData[0]);
      return {
        default: availableHeaders,
      };
    };

    const headersMap =
      rowData && rowData.length > 0 ? generateHeadersMap(rowData) : {};
    const selectedHeaders = headersMap.default || [];
    const dynamicColumns = generateDynamicColumns(rowData, selectedHeaders);
    const allColumns: ColDef[] = [...predefinedColumns, ...dynamicColumns];
    return allColumns;
  }, [rowData, selectedButton, businessUnitData]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    handleEditActivityReportChange(value);
    setIsOpen(true);
  };

  const handleEditReport = async () => {
    setLoading(true);
    const payload = {
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
      schedulerobj: {
        AccountId:
          businessUnitData?.accountId || LocalStorageUtil.getItem("accountId"),
      },
    };
    try {
      const response = await getActivityReportScheduleGrid(payload);
      setLoading(false);
      setSelectReport(response);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleEditActivityReportChange = async (value?: any) => {
    setScheduleReportId(value);
    setLoading(true);
    const payload = {
      contextName: LocalStorageUtil.getItem("contextName"),
      schedulerobj: {
        ScheduleReportId: value,
        Timezone: appSettings.timezone,
      },
    };
    try {
      const response = await scheduleActivityReportEdit(payload);
      setLoading(false);
      setActivityReport(response);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleClick = () => {
    setIsOpen((prev) => !prev);
    setFormData((prev) => ({ ...prev, scheduleReportId: "" }));
  };

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: appSettings.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const formatDate = (date: any) => {
    if (!date) return null;
    const parts = formatter.formatToParts(date).reduce((acc: any, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}`;
  };

  const handleButtonClick = (button: string) => {
    setSelectedButton(button);
  };

  const handleApplyClick = async () => {
    let valid = true;
    if (!startDate) {
      setStartDateError(true);
      valid = false;
    } else {
      setStartDateError(false);
    }
    if (!endDate) {
      setEndDateError(true);
      valid = false;
    } else {
      setEndDateError(false);
    }
    if (!valid) {
      return;
    }
    const payload = {
      Fromdate: formatDate(startDate),
      Todate: formatDate(endDate),
      Timezone: appSettings.timezone,
      contextName: LocalStorageUtil.getItem("contextName"),
      currentPage: 1,
      noOfRecords: 40000,
      sortColumn: "",
      sortOrder: 0,
    };
    setLoading(true);
    try {
      const response = await getActivityReportsData(payload);
      setLoading(false);
      setRowData(response.activityReportSummaryModel);
    } catch (error) {
      console.log("failed to load", error);
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setEndDateError(false);
    setStartDateError(false);
    setRowData([]);
  };

  const fetchReport = async () => {
    setLoading(true);
    const payload = {
      Timezone: "Asia/Calcutta",
      contextName: LocalStorageUtil.getItem("contextName"),
      currentPage: 1,
      noOfRecords: 25,
      sortColumn: "",
      sortOrder: 0,
    };
    try {
      await getActivityReportsData(payload);
      setLoading(false);
    } catch (error) {
      console.log("fail to load", error);
      setLoading(false);
    }
  };
  useEffect(() => {
    handleEditReport();
    fetchReport();
  }, []);

  const excelData = async () => {
    let valid = true;
    if (!startDate) {
      setStartDateError(true);
      valid = false;
    } else {
      setStartDateError(false);
    }
    if (!endDate) {
      setEndDateError(true);
      valid = false;
    } else {
      setEndDateError(false);
    }
    if (!valid) {
      return;
    }
    setLoading(true);
    const payload = {
      Fromdate: formatDate(startDate),
      Timezone: "Asia/Calcutta",
      Todate: formatDate(endDate),
      aliasname: "ActivitySummary",
      contextName:
        businessUnitData?.contextName ||
        LocalStorageUtil.getItem("contextName"),
      currentPage: 1,
      isexporttpExcel: true,
      noOfRecords: "25",
      offset: appSettings.timezone,
      pagename: "ActivitySummary",
      sortColumn: "",
      sortOrder: 0,
    };
    try {
      const excelDataResponse = await getExcelData(payload);
      setLoading(false);
      const blob = new Blob([excelDataResponse], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ActivityReportsData.xlsx");
      document.body.appendChild(link);
      link.click();
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error fetching Excel data", error);
      setLoading(false);
    }
  };
  const handleCallback = () => {
    setIsOpen(false);
    setFormData((prev) => ({ ...prev, scheduleReportId: "" }));
    handleEditReport();
  };
  return (
    <div style={{ overflow: "hidden" }}>
      <NavigationBar
        title="Activity Summary Reports"
        text="Activity Summary Reports"
      />
      <div>
        <Grid
          container
          spacing={2}
          sx={{
            marginTop: "2vh",
            display: "flex",
            justifyContent: "end",
          }}
          alignItems="center"
        >
          <Form.Group controlId="formCustomer" style={{ marginRight: "2vh" }}>
            <Form.Select
              name="scheduleReportId"
              value={formData.scheduleReportId}
              onChange={handleChange}
              data-testid="schedule-report-select"
              style={{
                fontSize: "11px",
              }}
            >
              <option value="">Select Report</option>
              {selectReport?.length > 0 &&
                selectReport.map((report: any) => (
                  <option
                    key={report?.scheduleReportId}
                    value={report?.scheduleReportId}
                  >
                    {report?.reportName}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
          {isPagePermission("Allow Scheduler View", "ActivityReport", 1388) && (
            <Button
              variant="contained"
              size="small"
              onClick={handleClick}
              sx={{
                backgroundColor: "var(--button-background-color)",
                color: "white",
                textTransform: "none",
                fontSize: "11px",
                padding: "1vh 1.4vh",
                height: "3vh",
                borderRadius: 0,
                marginRight: "3vh",
              }}
            >
              Schedule
              <IconButton
                size="small"
                sx={{ padding: 0, marginLeft: "0.8vh", color: "inherit" }}
              >
                {isOpen ? (
                  <ArrowDropUpIcon
                    fontSize="small"
                    data-testid="schedule-arrow-up"
                  />
                ) : (
                  <ArrowDropDownIcon
                    fontSize="small"
                    data-testid="schedule-arrow-down"
                  />
                )}
              </IconButton>
            </Button>
          )}
          {rowData.length > 0 &&
          !isOpen &&
          isPagePermission("Allow Export to Excel", "ActivityReport", 1381) ? (
            <img
              src={ExcelIcon}
              onClick={excelData}
              className="excel-icon"
              alt="Excel Icon"
              style={{ marginTop: ".3em" }}
              tabIndex={0}
              role="button" // NOSONAR
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  excelData();
                }
              }}
            />//NOSONAR
          ) : null}
        </Grid>
        {isOpen && (
          <ScheduleActivityReport
            editReportData={activityReport}
            scheduleReportId={scheduleReportId}
            setScheduleReportId={setScheduleReportId}
            setActivityReport={setActivityReport}
            handleCallback={handleCallback}
            isOpen={isOpen}
          />
        )}
      </div>
      {!isOpen ? (
        <Row style={{ marginLeft: "1em" }}>
          <Col>
            <Box>
              <div className="rp-main-inner col-sm-12">
                <div className="queue-top row clearfix">
                  <div
                    className="col-sm-8"
                    style={{
                      display: "flex",
                      gap: "0.5vh",
                      alignItems: "center",
                      marginTop: "2vh",
                      width: "66%",
                    }}
                  >
                    {isPagePermission(
                      "Allow Activity Summary",
                      "ActivityReport",
                      1378
                    ) && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleButtonClick("ACTIVITY SUMMARY")}
                        style={{
                          backgroundColor:
                            selectedButton === "ACTIVITY SUMMARY"
                              ? "grey"
                              : "var(--button-background-color)",
                          textTransform: "none",
                          fontSize: "11px",
                          paddingTop: "0.2vh",
                          paddingBottom: "0.5vh",
                          paddingLeft: "1vh",
                          marginBottom: "0.5vh",
                          whiteSpace: "normal",
                          borderTopLeftRadius: "0vh",
                          borderTopRightRadius: "0vh",
                          borderBottomLeftRadius: "0vh",
                          borderBottomRightRadius: "0vh",
                          borderRadius: "4px",
                          height: "25px",
                        }}
                      >
                        ACTIVITY SUMMARY
                      </Button>
                    )}
                    {isPagePermission(
                      "Allow Activity Detail",
                      "ActivityReport",
                      1379
                    ) && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleButtonClick("ACTIVITY DETAILS")}
                        style={{
                          backgroundColor:
                            selectedButton === "ACTIVITY DETAILS"
                              ? "grey"
                              : "var(--button-background-color)",
                          textTransform: "none",
                          fontSize: "11px",
                          paddingTop: "0.2vh",
                          paddingBottom: "0.5vh",
                          paddingLeft: "1vh",
                          marginBottom: "0.5vh",
                          whiteSpace: "normal",
                          borderTopLeftRadius: "0vh",
                          borderTopRightRadius: "0vh",
                          borderBottomLeftRadius: "0vh",
                          borderBottomRightRadius: "0vh",
                          borderRadius: "4px",

                          height: "25px",
                        }}
                      >
                        ACTIVITY DETAILS
                      </Button>
                    )}
                  </div>
                  <div
                    className="col-sm-12"
                    style={{
                      display: "flex",
                      gap: "0.5vh",
                      alignItems: "center",
                      marginTop: "2vh",
                    }}
                  >
                    <div
                      className="col-sm-8"
                      style={{
                        display: "flex",
                        gap: "0.625em",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        width: "66%",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <InputLabel
                          style={{
                            color: "var(--text-color)",
                            marginBottom: "0.25em",
                            fontWeight: 600,
                            fontSize: "11px",
                          }}
                        >
                          From Date: *
                        </InputLabel>
                        <Form.Control
                          type="date"
                          placeholder="From Date"
                          name="fromdate"
                          max={new Date().toISOString().split("T")[0]}
                          value={
                            startDate
                              ? startDate.toISOString().split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            setStartDate(new Date(e.target.value))
                          }
                          style={{
                            padding: ".3em",
                            width: "150px",
                            fontSize: "11px",
                            borderRadius: "0",
                            border: "1px solid #ccc",
                            borderColor: "black",
                            marginTop: ".1em",
                          }}
                        />
                        <div style={{ height: "1.25em" }}>
                          {startDateError && (
                            <FormHelperText error sx={{ marginTop: "0.25em" }}>
                              Please select date
                            </FormHelperText>
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <InputLabel
                          style={{
                            color: "var(--text-color)",
                            marginBottom: "0.25em",
                            fontWeight: 600,
                            fontSize: "11px",
                            marginLeft: ".5em",
                          }}
                        >
                          To Date: *
                        </InputLabel>
                        <Form.Control
                          type="date"
                          placeholder="To Date"
                          name="todate"
                          value={
                            endDate ? endDate.toISOString().split("T")[0] : ""
                          }
                          max={new Date().toISOString().split("T")[0]}
                          onChange={(e) => setEndDate(new Date(e.target.value))}
                          style={{
                            padding: ".3em",
                            width: "150px",
                            fontSize: "11px",
                            borderRadius: "0",
                            border: "1px solid #ccc",
                            borderColor: "black",
                            marginTop: ".1em",
                            marginLeft: ".5em",
                          }}
                        />
                        <div style={{ height: "1.25em" }}>
                          {endDateError && (
                            <FormHelperText error sx={{ marginTop: "0.25em" }}>
                              Please select date
                            </FormHelperText>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5em",
                          alignItems: "center",
                          marginTop: "1.3em",
                          fontSize: "14px",
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleApplyClick}
                          className="button apply-button"
                          style={{
                            marginLeft: ".5em",
                          }}
                        >
                          Apply
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleReset}
                          className="button reset-button"
                        >
                          Reset
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="queue-table-view clearfix">
                    <div
                      className="ag-theme-quartz-dark"
                      style={{
                        height: "58vh",
                        width: "99%",
                        marginTop: "2vh",
                      }}
                    >
                      <AgGridReact
                        columnDefs={columnDefs}
                        rowData={rowData}
                        pagination={true}
                        paginationPageSize={defaulPageSize}
                        paginationPageSizeSelector={pageSizeSelector}
                        rowGroupPanelShow="always"
                        pivotPanelShow="always"
                        gridOptions={{
                          rowSelection: {
                            mode: "multiRow",
                            checkboxes: true,
                            headerCheckbox: true,
                            enableClickSelection: false,
                          },
                        }}
                        defaultColDef={defaultColDef}
                        ref={gridRef}
                        loading={!rowData}
                        loadingOverlayComponent={CustomLoadingOverlay}
                        overlayNoRowsTemplate={
                          '<span class="ag-overlay-loading-center">No rows to show</span>'
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </Col>
        </Row>
      ) : (
        <></>
      )}
      <ActivityReportDetailModal
        open={openDetailsModal}
        onClose={handleModalClose}
        selectedData={selectedData}
      />
    </div>
  );
};

export default ActivityReport;
